import { useState, useMemo } from "react";
import {
  Search,
  Filter,
  Phone,
  Mail,
  MoreHorizontal,
  Building2,
  Star,
  ChevronDown,
  Edit,
  Trash2,
  MessageSquare,
  ArrowUpDown,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { useContacts, useDeleteContact } from "@/hooks/useContacts";
import { Skeleton } from "@/components/ui/skeleton";
import { AddContactDialog } from "@/components/contacts/AddContactDialog";
import { EditContactDialog } from "@/components/contacts/EditContactDialog";
import { ContactDetailSheet } from "@/components/contacts/ContactDetailSheet";
import { ContactStats } from "@/components/contacts/ContactStats";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScheduleCallDialog } from "@/components/calls/ScheduleCallDialog";
import { SendWhatsAppDialog } from "@/components/outreach/SendWhatsAppDialog";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ContactWithAccount {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  position_title: string | null;
  is_primary_decision_maker: boolean | null;
  account_id: string | null;
  accounts: { name: string } | null;
}

type SortField = "name" | "account" | "title" | "email";
type SortOrder = "asc" | "desc";

export default function Contacts() {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDecisionMakers, setFilterDecisionMakers] = useState(false);
  const [filterWithEmail, setFilterWithEmail] = useState(false);
  const [filterWithPhone, setFilterWithPhone] = useState(false);
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [scheduleCallOpen, setScheduleCallOpen] = useState(false);
  const [whatsAppOpen, setWhatsAppOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactWithAccount | null>(null);
  
  const { data: contacts, isLoading, error } = useContacts();
  const deleteContact = useDeleteContact();

  // Calculate stats
  const stats = useMemo(() => {
    if (!contacts) return { totalContacts: 0, decisionMakers: 0, withEmail: 0, withPhone: 0 };
    return {
      totalContacts: contacts.length,
      decisionMakers: contacts.filter(c => c.is_primary_decision_maker).length,
      withEmail: contacts.filter(c => c.email).length,
      withPhone: contacts.filter(c => c.phone).length,
    };
  }, [contacts]);

  // Filter and sort contacts
  const filteredAndSortedContacts = useMemo(() => {
    let result = (contacts as ContactWithAccount[] | undefined) || [];

    // Apply search
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      result = result.filter((contact) =>
        contact.first_name?.toLowerCase().includes(search) ||
        contact.last_name?.toLowerCase().includes(search) ||
        contact.email?.toLowerCase().includes(search) ||
        contact.accounts?.name?.toLowerCase().includes(search)
      );
    }

    // Apply filters
    if (filterDecisionMakers) {
      result = result.filter(c => c.is_primary_decision_maker);
    }
    if (filterWithEmail) {
      result = result.filter(c => c.email);
    }
    if (filterWithPhone) {
      result = result.filter(c => c.phone);
    }

    // Apply sorting
    result = [...result].sort((a, b) => {
      let aValue: string;
      let bValue: string;

      switch (sortField) {
        case "name":
          aValue = `${a.first_name} ${a.last_name}`;
          bValue = `${b.first_name} ${b.last_name}`;
          break;
        case "account":
          aValue = a.accounts?.name || "";
          bValue = b.accounts?.name || "";
          break;
        case "title":
          aValue = a.position_title || "";
          bValue = b.position_title || "";
          break;
        case "email":
          aValue = a.email || "";
          bValue = b.email || "";
          break;
        default:
          aValue = "";
          bValue = "";
      }

      const comparison = aValue.localeCompare(bValue);
      return sortOrder === "asc" ? comparison : -comparison;
    });

    return result;
  }, [contacts, searchQuery, filterDecisionMakers, filterWithEmail, filterWithPhone, sortField, sortOrder]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handlePhoneClick = (contact: ContactWithAccount) => {
    if (contact.phone) {
      setSelectedContact(contact);
      setScheduleCallOpen(true);
    } else {
      toast.error("No phone number available for this contact");
    }
  };

  const handleMailClick = (contact: ContactWithAccount) => {
    if (contact.phone) {
      setSelectedContact(contact);
      setWhatsAppOpen(true);
    } else if (contact.email) {
      window.location.href = `mailto:${contact.email}`;
    } else {
      toast.error("No contact information available");
    }
  };

  const handleEditClick = (contact: ContactWithAccount) => {
    setSelectedContact(contact);
    setEditDialogOpen(true);
  };

  const handleViewClick = (contact: ContactWithAccount) => {
    setSelectedContact(contact);
    setDetailSheetOpen(true);
  };

  const handleDeleteClick = (contact: ContactWithAccount) => {
    setSelectedContact(contact);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedContact) {
      deleteContact.mutate(selectedContact.id);
      setDeleteDialogOpen(false);
      setDetailSheetOpen(false);
      setSelectedContact(null);
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">Error loading contacts: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Contacts</h1>
          <p className="text-muted-foreground">
            Manage your business contacts
          </p>
        </div>
        <AddContactDialog />
      </div>

      {/* Stats */}
      <ContactStats {...stats} />

      {/* Filters Bar */}
      <div className="glass rounded-xl p-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search contacts..."
              className="pl-10 bg-muted/50 border-border"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Filter className="h-4 w-4" />
                Filters
                {(filterDecisionMakers || filterWithEmail || filterWithPhone) && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary text-primary-foreground rounded">
                    {[filterDecisionMakers, filterWithEmail, filterWithPhone].filter(Boolean).length}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuCheckboxItem
                checked={filterDecisionMakers}
                onCheckedChange={setFilterDecisionMakers}
              >
                Decision Makers Only
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={filterWithEmail}
                onCheckedChange={setFilterWithEmail}
              >
                Has Email
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={filterWithPhone}
                onCheckedChange={setFilterWithPhone}
              >
                Has Phone
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="glass rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : !filteredAndSortedContacts || filteredAndSortedContacts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-lg mb-2">No contacts found</p>
            <p className="text-sm">Add your first contact to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border/50 bg-muted/30">
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    <button
                      onClick={() => toggleSort("name")}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      Contact
                      <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    <button
                      onClick={() => toggleSort("account")}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      Account
                      <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    <button
                      onClick={() => toggleSort("title")}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      Title
                      <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    <button
                      onClick={() => toggleSort("email")}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      Email
                      <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    Phone
                  </th>
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {filteredAndSortedContacts.map((contact) => (
                  <tr
                    key={contact.id}
                    className="hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => handleViewClick(contact)}
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback className="bg-primary/10 text-primary text-sm">
                            {contact.first_name?.[0]}{contact.last_name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-foreground">
                            {contact.first_name} {contact.last_name}
                          </p>
                          {contact.is_primary_decision_maker && (
                            <Star className="h-3.5 w-3.5 fill-warning text-warning" />
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      {contact.accounts ? (
                        <div className="flex items-center gap-2 text-sm">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span>{contact.accounts.name}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">No account</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm text-muted-foreground">
                        {contact.position_title || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm text-muted-foreground">
                        {contact.email || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-sm text-muted-foreground">
                        {contact.phone || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-primary/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePhoneClick(contact);
                          }}
                          disabled={!contact.phone}
                        >
                          <Phone className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-primary/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMailClick(contact);
                          }}
                          disabled={!contact.phone && !contact.email}
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              handleViewClick(contact);
                            }}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(contact);
                            }}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Contact
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              handlePhoneClick(contact);
                            }} disabled={!contact.phone}>
                              <Phone className="h-4 w-4 mr-2" />
                              Schedule Call
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              handleMailClick(contact);
                            }} disabled={!contact.phone && !contact.email}>
                              <MessageSquare className="h-4 w-4 mr-2" />
                              Send Message
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(contact);
                              }}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Contact
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredAndSortedContacts && filteredAndSortedContacts.length > 0 && (
          <div className="px-5 py-4 border-t border-border/50 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Showing {filteredAndSortedContacts.length} contact{filteredAndSortedContacts.length !== 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled>
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled>
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Contact Dialog */}
      <EditContactDialog
        contact={selectedContact}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
      />

      {/* Contact Detail Sheet */}
      <ContactDetailSheet
        contact={selectedContact}
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
        onEdit={() => {
          setDetailSheetOpen(false);
          setEditDialogOpen(true);
        }}
        onDelete={() => {
          setDetailSheetOpen(false);
          handleDeleteClick(selectedContact!);
        }}
        onScheduleCall={() => {
          setDetailSheetOpen(false);
          if (selectedContact) handlePhoneClick(selectedContact);
        }}
        onSendMessage={() => {
          setDetailSheetOpen(false);
          if (selectedContact) handleMailClick(selectedContact);
        }}
      />

      {/* Schedule Call Dialog */}
      <ScheduleCallDialog 
        open={scheduleCallOpen} 
        onOpenChange={setScheduleCallOpen}
        preSelectedContactId={selectedContact?.id}
      />

      {/* WhatsApp Dialog */}
      <SendWhatsAppDialog 
        open={whatsAppOpen} 
        onOpenChange={setWhatsAppOpen}
        contactId={selectedContact?.id}
        contactPhone={selectedContact?.phone || undefined}
        contactName={selectedContact ? `${selectedContact.first_name} ${selectedContact.last_name}` : undefined}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedContact?.first_name} {selectedContact?.last_name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}